import React, {Component} from "react";
import OitSummaryLoading from "./OitSummaryLoading";
import {ApiGet, ApiPost, promptAiText} from "../../../shared/services/api-service";
import {normalizeOitReport} from "../../services/analytics";
import {connect} from "react-redux";
import {Storage} from "../../../shared/services/aws";
import OitSummaryReport from "./OitSummaryReport";
import taskTrainingActionCreators from "../../store/task-training/action-creators";
import notificationsActionsCreator from "../../store/notifications/action-creators";
import {createSubgroups} from "../../../shared/services/utils/tasks";
import {getAverageByForm} from "../../services/api/statistics";

let collator = new Intl.Collator(undefined, {numeric: true, sensitivity: "base"});

const getPrompt = (aiInput) => {
  return `
SYSTEM / ROLE
You are "FTO AI Reporting - Narrative JSON Builder." Use the embedded calculated report data to write only narrative/enrichment content.

IMPORTANT
- Do NOT generate an image.
- Do NOT call any APIs.
- Output ONLY valid JSON (no markdown).
- Do NOT change calculated counts, ratings, task totals, or statuses.
- Never output case numbers/event numbers.
- Use performance_exceptions as the only source for deficiencies.
- Use unfinished_task_groups as the source for task progress assessment.
- When callLogTracking.enabled is false, do NOT infer call exposure gaps, officer safety exposure, or exposure goals.
- Treat commandSummary.programStatus as a preliminary estimate, not a final personnel judgment.
- Do NOT change the calculated program status label, tone, confidence, or supporting evidence.
- Describe program status with balanced language that mentions both positive and concerning evidence when both are present.
- Base the program status overview on the available performance level, phase trend, department comparison, and task progress; omit any unavailable factor.
- Acknowledge limited data in the program status narrative when confidence is limited.

INPUT JSON (EMBEDDED)
<<INPUT_JSON>>
${JSON.stringify(aiInput)}
<</INPUT_JSON>>

Return ONE JSON object with exactly these top-level keys:
{
  "programStatus": {
    "summary": "Two or three sentences describing the estimated program progress without making a final personnel judgment.",
    "keyFactors": ["Concise factor grounded in the calculated evidence."],
    "confidenceNote": "Short explanation of the estimate's data coverage or limitations."
  },
  "executiveOverview": {
    "strengths": ["Short bullet."],
    "improvements": ["Short bullet."],
    "weaknesses": ["Short bullet."],
    "concerns": ["Short bullet."],
    "focusAreas": ["Short bullet."]
  },
  "deficiencies": {
    "items": [
      { "category": "Report Writing", "count": 4, "mostRecent": "YYYY-MM-DD", "bullets": ["Short chief-level observation."] }
    ],
    "topRecurring": ["Report Writing", "Officer Safety"]
  },
  "tasks": {
    "assessment": "Concise assessment of calculated task progress.",
    "progressRisks": ["Risk based on unfinished groups/tasks."],
    "recommendedFocus": ["Concrete focus area."],
    "nextPhaseReadiness": "Short readiness statement."
  },
  "chiefNotes": [
    { "type": "attention", "category": "Needs paying attention", "text": "Concise bullet." },
    { "type": "risk", "category": "Risks", "text": "Concise bullet." },
    { "type": "positive", "category": "Commendations", "text": "Concise bullet." },
    { "type": "suggestion", "category": "Suggestions for next phase", "text": "Concise bullet." }
  ],
  "riskIndicators": [
    { "type": "risk|positive", "text": "Short risk or positive indicator." }
  ],
  "recommendations": [
    { "title": "Recommendation title", "type": "attention|risk|positive|suggestion", "text": "Recommendation text." }
  ],
  "nextPhaseFocus": {
    "developmentPriorities": ["Top priority."],
    "exposureGoals": [{ "name": "Felony Arrests", "target": "1-2" }],
    "trainingRemediation": ["Scenario training: Searches"]
  },
  "overallAssessment": {
    "rating": "On Track|Monitor Closely|At Risk",
    "summary": "Chief-level assessment of the recruit.",
    "bullets": ["Short supporting point."]
  }
}
  `;
};

const toNumber = (value) => {
  let num = Number(value);
  return isNaN(num) ? null : num;
};

const roundNumber = (value, precision) => {
  let num = toNumber(value);
  if (num === null) {
    return null;
  }
  let factor = Math.pow(10, precision || 2);
  return Math.round(num * factor) / factor;
};

const ratingLabel = (avg) => {
  let num = toNumber(avg);
  if (num === null) {
    return "N/A";
  }
  if (num < 2.5) {
    return "Below Expectations";
  }
  if (num < 3.5) {
    return "Meets Expectations";
  }
  if (num < 4.25) {
    return "Exceeds Expectations";
  }
  return "Superior";
};

const ratingFlag = (score) => {
  let num = toNumber(score);
  if (num === null) {
    return "Not Yet Rated";
  }
  if (num < 2.5) {
    return "Performance Concern";
  }
  if (num < 3) {
    return "Development Focus";
  }
  return "OK";
};

const percent = (count, total) => {
  if (!total) {
    return 0;
  }
  return roundNumber((count / total) * 100, 1);
};

const textIncludesAny = (text, keywords) => {
  let source = String(text || "").toLowerCase();
  return !!keywords.find(keyword => source.includes(keyword.toLowerCase()));
};

const inferActivityName = (value) => {
  let source = String(value || "");
  if (textIncludesAny(source, ["Traffic", "Stop", "Radar", "Lidar"])) return "Traffic Enforcement";
  if (textIncludesAny(source, ["Domestic", "Family", "FV"])) return "Domestic / Family";
  if (textIncludesAny(source, ["Assault", "Fight", "Battery"])) return "Violence / Assault";
  if (textIncludesAny(source, ["Robbery", "Armed"])) return "Robbery";
  if (textIncludesAny(source, ["Gun", "Shots Fired", "Discharging"])) return "Weapons / Shots Fired";
  if (textIncludesAny(source, ["Burglary", "Alarm"])) return "Burglary / Alarm";
  if (textIncludesAny(source, ["Theft", "Fraud"])) return "Theft / Fraud";
  if (textIncludesAny(source, ["Crash", "Accident", "Hit & Run"])) return "Traffic Crash";
  if (textIncludesAny(source, ["Suspicious", "Check"])) return "Suspicious / Check";
  if (textIncludesAny(source, ["Noise", "Disturbance"])) return "Disturbance / Quality of Life";
  return source || "Other";
};

const liabilityLevel = (activityName) => {
  if (textIncludesAny(activityName, ["Domestic", "Shots Fired", "Gun", "Robbery", "Armed", "Stabbing", "Homicide", "DWI", "FV", "Injury to a Child", "Discharging"])) {
    return "High";
  }
  if (textIncludesAny(activityName, ["Traffic Violation", "Noise", "Alarm", "Information", "Welfare Check"])) {
    return "Low";
  }
  return "Moderate";
};

const emptyAiEnhancement = () => ({
  programStatus: {
    summary: "",
    keyFactors: [],
    confidenceNote: ""
  },
  executiveOverview: {
    strengths: [],
    improvements: [],
    weaknesses: [],
    concerns: [],
    focusAreas: []
  },
  deficiencies: {items: [], topRecurring: []},
  tasks: {
    assessment: "",
    progressRisks: [],
    recommendedFocus: [],
    nextPhaseReadiness: ""
  },
  chiefNotes: [],
  riskIndicators: [],
  recommendations: [],
  nextPhaseFocus: {
    developmentPriorities: [],
    exposureGoals: [],
    trainingRemediation: []
  },
  overallAssessment: {
    rating: "N/A",
    summary: "",
    bullets: []
  }
});

const normalizePhaseName = (name) => String(name || "").trim().toLowerCase();

const filenameSegment = (value) => String(value || "")
  .trim()
  .replace(/[^a-z0-9_-]+/gi, "_")
  .replace(/^_+|_+$/g, "");

class OitSummaryPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      reportAnalytics: null,
      isLoading: true,
      isPdfLibraryReady: false,
      isSavingPdf: false,
      selectedFormTypes: (props.settings && props.settings.oitAnalyticsFormTypes) || ["dor", "dor2", "dor3", "rdor", "wor", "dorr"]
    };
    this.html2pdf = null;
    this.reportRef = React.createRef();
  }

  async componentDidMount() {
    this.loadPdfLibrary();
    await this.init();
  }

  async loadPdfLibrary() {
    try {
      let html2pdfModule = await import(/* webpackChunkName: "html2pdf" */ "html2pdf.js");
      this.html2pdf = html2pdfModule.default || html2pdfModule;
      this.setState({isPdfLibraryReady: true});
    } catch (err) {
      console.log("OIT summary PDF library error", err);
      this.props.addError("Unable to load the PDF tools. Please reopen the report and try again.");
    }
  }

  getPdfFilename() {
    let traineeName = this.props.oit && this.props.oit.getFullName
      ? this.props.oit.getFullName()
      : "Trainee";
    let phaseName = (this.props.phase && this.props.phase.header) || "Phase";
    let date = new Date().toISOString().slice(0, 10);
    return [traineeName, phaseName, "Summary", date]
      .map(filenameSegment)
      .filter(segment => segment)
      .join("_") + ".pdf";
  }

  async waitForReportAssets(reportElement) {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    let images = Array.from(reportElement.querySelectorAll("img"));
    await Promise.all(images.map(image => {
      if (image.complete) {
        return Promise.resolve();
      }
      return new Promise(resolve => {
        image.addEventListener("load", resolve, {once: true});
        image.addEventListener("error", resolve, {once: true});
      });
    }));
  }

  async savePdf() {
    if (this.state.isSavingPdf || !this.state.isPdfLibraryReady || !this.reportRef.current) {
      return;
    }

    let reportElement = this.reportRef.current.querySelector(".oit-summary-report");
    if (!reportElement) {
      this.props.addError("Unable to find the report content to save.");
      return;
    }

    this.setState({isSavingPdf: true});
    try {
      await this.waitForReportAssets(reportElement);
      let filename = this.getPdfFilename();
      let pdfBlob = await this.html2pdf()
        .set({
          margin: 0.2,
          filename,
          image: {type: "png"},
          html2canvas: {
            scale: 2.5,
            useCORS: true,
            logging: false,
            backgroundColor: "#F3F6FA",
            windowWidth: Math.max(reportElement.scrollWidth, 1440)
          },
          jsPDF: {unit: "in", format: "a3", orientation: "landscape"},
          pagebreak: {mode: ["css", "legacy"], avoid: [".oit-summary-report-card"]}
        })
        .from(reportElement)
        .outputPdf("blob");

      let timestamp = Date.now();
      let s3Key = `${this.props.userContext.departmentGuid}/reports/${timestamp}/${filename}`;
      let uploadedPdf = await Storage.put(s3Key, pdfBlob, {contentType: "application/pdf"});
      let phaseGuid = this.props.phase.guid || this.props.phase.trainingPhaseGuid;

      await ApiPost(`/api/v1/training-phases/${phaseGuid}/files`, {
        training_phase_file: {
          file: {
            name: uploadedPdf.key,
            displayName: filename
          },
          date: new Date()
        }
      });
      if (this.props.onSaved) {
        this.props.onSaved();
      }
      this.props.addSavedSuccessfully("Phase summary PDF saved to phase files");
    } catch (err) {
      console.log("OIT summary PDF save error", err);
      this.props.addError("Unable to save the phase summary PDF. Please try again.");
    } finally {
      this.setState({isSavingPdf: false});
    }
  }

  async init() {
    let oit = this.props.oit;
    let phase = this.props.phase;
    let oitGuid = oit.guid;
    let callLogsTracked = this.departmentTracksCallLogs();

    try {
      await this.props.fetchTaskTraining(oitGuid);

      let analyticsResponse = await ApiGet(`/api/v1/oits/${oitGuid}/training-phase-analytics/${this.state.selectedFormTypes.join(",")}`);
      let reportAnalytics = normalizeOitReport(analyticsResponse, false);
      let groupedLogs = callLogsTracked ? this.buildGroupedLogs(reportAnalytics, oit) : [];
      let phaseObservationAverages = this.buildPhaseObservationAverages(reportAnalytics, oit);
      let people = this.buildPeople(oit, phase);
      let ratingStatistics = await this.buildRatingStatistics(reportAnalytics, oit);
      let performance = this.buildPerformance(phaseObservationAverages, oit, phase);
      let tasks = this.buildTasks(oit, phase);
      let agencyLogo = await this.getAgencyLogo();

      let calculatedReport = {
        meta: {
          agencyName: this.props.userContext.departmentName,
          agencyLogo,
          programName: oit.trainingTracker.trainingProgram.title,
          asOf: phase.openedAt,
          renderVersion: "1.0"
        },
        people,
        summaryBadges: this.buildSummaryBadges(performance, people),
        callLogsTracked,
        callExposure: this.buildCallExposure(groupedLogs),
        performance,
        ratingStatistics,
        deficiencies: {items: [], topRecurring: []},
        tasks: tasks.report,
        chiefNotes: [],
        recommendations: [],
        overallAssessment: emptyAiEnhancement().overallAssessment,
        uiHints: {
          layout: "chief-one-pager",
          charts: {
            callLiability: "pie",
            observations: "groupedBar",
            callsByActivity: "bar",
            callsByArrestStatus: "donut",
            activityByArrest: "stackedBar",
            callsByDisposition: "bar"
          }
        }
      };
      calculatedReport.commandSummary = this.buildCommandSummary(calculatedReport, reportAnalytics, oit, phase);
      calculatedReport.programStatus = calculatedReport.commandSummary.programStatus;
      calculatedReport.executiveOverview = calculatedReport.commandSummary.executiveOverview;
      calculatedReport.performanceTrend = calculatedReport.commandSummary.performanceTrend;
      calculatedReport.performanceExceptions = calculatedReport.commandSummary.performanceExceptions;
      calculatedReport.criticalExposure = calculatedReport.commandSummary.criticalExposure;
      calculatedReport.officerSafetyExposure = calculatedReport.commandSummary.officerSafetyExposure;
      calculatedReport.riskIndicators = calculatedReport.commandSummary.riskIndicators;
      calculatedReport.commandStaffNotes = calculatedReport.commandSummary.commandStaffNotes;
      calculatedReport.nextPhaseFocus = calculatedReport.commandSummary.nextPhaseFocus;

      let aiInput = {
        meta: calculatedReport.meta,
        people: calculatedReport.people,
        commandSummary: calculatedReport.commandSummary,
        summaryBadges: calculatedReport.summaryBadges,
        callLogTracking: {enabled: callLogsTracked},
        ...(callLogsTracked ? {
          callExposureSummary: {
            totalCalls: calculatedReport.callExposure.totalCalls,
            topActivities: calculatedReport.callExposure.topActivities,
            liabilityBreakdown: calculatedReport.callExposure.liabilityBreakdown
          }
        } : {}),
        performance: calculatedReport.performance,
        performance_exceptions: reportAnalytics.performanceExceptions || [],
        tasks: {
          status: calculatedReport.tasks.status,
          completion: calculatedReport.tasks.completion,
          incompleteChecklist: calculatedReport.tasks.incompleteChecklist,
          unfinished_task_groups: tasks.aiGroups
        }
      };

      let report = await this.buildAiEnhancedReport(calculatedReport, aiInput);

      this.setState({
        isLoading: false,
        report,
        reportAnalytics
      });
    } catch (err) {
      console.log("OIT summary report error", err);
      let fallback = {
        ...emptyAiEnhancement(),
        meta: {agencyName: this.props.userContext.departmentName, asOf: phase.openedAt, renderVersion: "1.0"},
        people: this.buildPeople(oit, phase),
        summaryBadges: [],
        callLogsTracked,
        callExposure: this.buildCallExposure([]),
        performance: this.emptyPerformance(),
        tasks: {status: "N/A", completion: {done: 0, total: 0, ratio: null}, incompleteChecklist: []}
      };
      fallback.commandSummary = this.buildCommandSummary(fallback, {}, oit, phase);
      fallback.programStatus = fallback.commandSummary.programStatus;
      fallback.executiveOverview = fallback.commandSummary.executiveOverview;
      fallback.performanceTrend = fallback.commandSummary.performanceTrend;
      fallback.performanceExceptions = fallback.commandSummary.performanceExceptions;
      fallback.criticalExposure = fallback.commandSummary.criticalExposure;
      fallback.officerSafetyExposure = fallback.commandSummary.officerSafetyExposure;
      fallback.riskIndicators = fallback.commandSummary.riskIndicators;
      fallback.commandStaffNotes = fallback.commandSummary.commandStaffNotes;
      fallback.nextPhaseFocus = fallback.commandSummary.nextPhaseFocus;
      this.setState({
        isLoading: false,
        report: fallback
      });
    }
  }

  async buildAiEnhancedReport(calculatedReport, aiInput) {
    try {
      let response = await promptAiText({
        contents: getPrompt(aiInput)
      });
      let aiEnhancement = this.parseAiEnhancement(response);
      let aiProgramStatus = aiEnhancement.programStatus || {};

      let enhancedReport = {
        ...calculatedReport,
        programStatus: {
          ...calculatedReport.programStatus,
          summary: aiProgramStatus.summary || calculatedReport.programStatus.summary,
          keyFactors: (aiProgramStatus.keyFactors && aiProgramStatus.keyFactors.length)
            ? aiProgramStatus.keyFactors
            : calculatedReport.programStatus.keyFactors,
          confidenceNote: aiProgramStatus.confidenceNote || calculatedReport.programStatus.confidenceNote
        },
        executiveOverview: {
          ...calculatedReport.executiveOverview,
          ...(aiEnhancement.executiveOverview || {})
        },
        deficiencies: aiEnhancement.deficiencies || calculatedReport.deficiencies,
        tasks: {
          ...calculatedReport.tasks,
          ...(aiEnhancement.tasks || {})
        },
        riskIndicators: (aiEnhancement.riskIndicators && aiEnhancement.riskIndicators.length)
          ? aiEnhancement.riskIndicators
          : calculatedReport.riskIndicators,
        chiefNotes: aiEnhancement.chiefNotes || calculatedReport.chiefNotes || [],
        commandStaffNotes: aiEnhancement.chiefNotes || calculatedReport.commandStaffNotes || [],
        recommendations: aiEnhancement.recommendations || [],
        nextPhaseFocus: {
          ...calculatedReport.nextPhaseFocus,
          ...(aiEnhancement.nextPhaseFocus || {})
        },
        overallAssessment: aiEnhancement.overallAssessment || emptyAiEnhancement().overallAssessment
      };
      if (!calculatedReport.callLogsTracked) {
        enhancedReport.nextPhaseFocus.exposureGoals = [];
      }
      return enhancedReport;
    } catch (err) {
      console.log("OIT summary AI enrichment error", err);
      return {
        ...calculatedReport,
        deficiencies: calculatedReport.deficiencies || emptyAiEnhancement().deficiencies,
        chiefNotes: calculatedReport.chiefNotes || [],
        recommendations: calculatedReport.recommendations || [],
        tasks: calculatedReport.tasks
      };
    }
  }

  departmentTracksCallLogs() {
    return (this.props.forms || []).some(form => {
      return form.status === "current"
        && (form.layout || []).some(field => field.schema === "log-field");
    });
  }

  async getAgencyLogo() {
    if (!this.props.settings || !this.props.settings.departmentBadge) {
      return null;
    }

    try {
      return await Storage.get(this.props.settings.departmentBadge);
    } catch (err) {
      console.log("OIT summary badge lookup error", err);
      return null;
    }
  }

  parseAiEnhancement(response) {
    let result = response && response.data ? response.data.result : response;
    if (typeof result === "object") {
      return result || emptyAiEnhancement();
    }

    try {
      return JSON.parse(String(result || "").replace("```json", "").replace("```", ""));
    } catch (err) {
      console.log("OIT summary AI parse error", err);
      return emptyAiEnhancement();
    }
  }

  buildPeople(oit, phase) {
    let ftoName = phase.fto ? `${phase.fto.firstName} ${phase.fto.lastName}` : "No FTO assigned";
    let completedPhase = this.getCompletedPhaseName(oit, phase);
    let observationPhases = (oit.trainingPhases || []).filter(tp => tp.type === "observation");
    let currentIndex = observationPhases.findIndex(tp => tp.trainingPhaseGuid === phase.trainingPhaseGuid || tp.guid === phase.guid);
    let badgeId = oit.badgeNumber || oit.badge_number || oit.employeeId || oit.employee_id || oit.cadId || oit.cad_id || oit.userId || oit.user_id || "N/A";

    return {
      traineeName: oit.getFullName(),
      badgeId,
      ftoName,
      currentPhase: phase.header,
      completedPhase,
      phaseNumber: currentIndex > -1 ? currentIndex + 1 : null,
      phaseTotal: observationPhases.length || null,
      phaseOpenedAt: phase.openedAt,
      phaseClosedAt: phase.closedAt || phase.completedAt || phase.endsAt || phase.endDate || null
    };
  }

  getCompletedPhaseName(oit, phase) {
    let observationPhases = (oit.trainingPhases || []).filter(tp => tp.type === "observation");
    let currentIndex = observationPhases.findIndex(tp => tp.trainingPhaseGuid === phase.trainingPhaseGuid);
    if (currentIndex > 0) {
      return observationPhases[currentIndex - 1].header;
    }

    let completed = observationPhases
      .filter(tp => tp.status === "completed")
      .sort((a, b) => collator.compare(a.header, b.header));

    return completed.length ? completed[completed.length - 1].header : "N/A";
  }

  buildGroupedLogs(reportAnalytics, oit) {
    let logs = [];
    (reportAnalytics.logReport || []).forEach(logReport => {
      this.getGroups(logReport)
        .forEach(group => {
          let groupedLogs = this.groupLogs(logReport, group, oit);
          let rows = groupedLogs
            .filter(lr => lr.count)
            .map(lr => ({
              type: lr.key,
              count: lr.count,
              pct: this.percentage(groupedLogs, lr.count)
            }));

          if (rows.length) {
            logs.push({
              name: this.getGroupName(logReport, group),
              rows
            });
          }
        });
    });
    return logs;
  }

  buildPhaseObservationAverages(reportAnalytics, oit) {
    return Object.keys(reportAnalytics.phaseAvgMap || {})
      .map(key => {
        let [phaseGuid, question] = key.split("#");
        let phase = (oit.trainingPhases || []).find(tp => tp.trainingPhaseGuid === phaseGuid);
        if (!phase) {
          return null;
        }
        return {
          phase: phase.header,
          observation: question? question.split(/(\r\n|\r|\n|\s-)/)[0]: question,
          avg_score: roundNumber(reportAnalytics.phaseAvgMap[key].avgRating, 2)
        };
      })
      .filter(row => row);
  }

  buildPerformance(phaseObservationAverages, oit, phase) {
    let observationPhases = (oit.trainingPhases || []).filter(tp => tp.type === "observation");
    let currentPhaseIndex = observationPhases.findIndex(tp => tp.trainingPhaseGuid === phase.trainingPhaseGuid);
    if (currentPhaseIndex < 0) {
      currentPhaseIndex = observationPhases.findIndex(tp => normalizePhaseName(tp.header) === normalizePhaseName(phase.header));
    }
    let previousPhase = currentPhaseIndex > 0 ? observationPhases[currentPhaseIndex - 1] : null;
    let previousPhaseName = previousPhase ? previousPhase.header : null;
    let currentPhaseName = phase.header;
    let phaseScores = phaseObservationAverages.reduce((acc, row) => {
      let phaseMap = acc[row.phase] || {};
      phaseMap[row.observation] = row.avg_score;
      return {
        ...acc,
        [row.phase]: phaseMap
      };
    }, {});

    let buildPhaseAverage = (phaseName) => {
      let scores = Object.keys(phaseScores[phaseName] || {})
        .map(key => phaseScores[phaseName][key])
        .filter(score => score !== null);
      let overallAvg = scores.length ? roundNumber(scores.reduce((sum, score) => sum + score, 0) / scores.length, 2) : null;
      return {
        phase: phaseName,
        overallAvg,
        label: ratingLabel(overallAvg),
        delta: null
      };
    };

    let previousAvg = previousPhaseName ? buildPhaseAverage(previousPhaseName) : null;
    let currentAvg = buildPhaseAverage(currentPhaseName);
    currentAvg.delta = previousAvg && currentAvg.overallAvg !== null && previousAvg.overallAvg !== null
      ? roundNumber(currentAvg.overallAvg - previousAvg.overallAvg, 2)
      : null;
    let phaseAverages = [previousAvg, currentAvg].filter(row => row);

    let previousScores = previousPhaseName ? phaseScores[previousPhaseName] || {} : {};
    let currentScores = phaseScores[currentPhaseName] || {};
    let observationNames = Object.keys({...previousScores, ...currentScores});

    let categories = observationNames.map(name => {
      let previousScore = previousScores[name];
      let currentScore = currentScores[name];
      return {
        name,
        previousPhase: previousScore === undefined ? null : previousScore,
        currentPhase: currentScore === undefined ? null : currentScore,
        delta: previousScore !== undefined && currentScore !== undefined && previousScore !== 0 && currentScore !== 0 ? roundNumber(currentScore - previousScore, 2) : null,
        flag: ratingFlag(currentScore)
      };
    });

    let deltas = Object.keys({...previousScores, ...currentScores})
      .map(name => {
        if (previousScores[name] === undefined || currentScores[name] === undefined) {
          return null;
        }
        return {name, delta: roundNumber(currentScores[name] - previousScores[name], 2)};
      })
      .filter(row => row);

    return {
      previousPhaseName,
      currentPhaseName,
      phaseAverages,
      observationBars: {
        scale: {min: 1, max: 5},
        previousPhaseName,
        currentPhaseName,
        categories
      },
      deltas: {
        overallDelta: currentAvg.delta,
        topImprovements: deltas
          .filter(row => row.delta > 0)
          .sort((a, b) => b.delta - a.delta)
          .slice(0, 3),
        topDeclines: deltas
          .filter(row => row.delta < 0)
          .sort((a, b) => a.delta - b.delta)
          .slice(0, 3),
        notYetRatedCurrentPhase: observationNames.filter(name => currentScores[name] === undefined || currentScores[name] === null)
      }
    };
  }

  emptyPerformance() {
    return {
      previousPhaseName: null,
      currentPhaseName: null,
      phaseAverages: [],
      observationBars: {
        scale: {min: 1, max: 5},
        previousPhaseName: null,
        currentPhaseName: null,
        categories: []
      },
      deltas: {
        overallDelta: null,
        topImprovements: [],
        topDeclines: [],
        notYetRatedCurrentPhase: []
      }
    };
  }

  buildSummaryBadges(performance, people) {
    let currentPhaseAvg = (performance.phaseAverages || []).find(row => row.phase === people.currentPhase);
    let previousPhaseAvg = (performance.phaseAverages || []).find(row => row.phase === performance.previousPhaseName);
    let currentRatedCount = ((performance.observationBars && performance.observationBars.categories) || [])
      .filter(row => row.currentPhase !== null && row.currentPhase !== undefined)
      .length;
    let ratingSource = currentRatedCount >= 3 ? currentPhaseAvg : previousPhaseAvg;
    let badges = [
      {
        label: "CURRENT RATING",
        value: ratingSource ? ratingSource.label : "N/A",
        note: currentRatedCount >= 3 ? undefined : "Using previous phase"
      }
    ];

    (performance.phaseAverages || []).forEach(row => {
      let deltaNote = row.delta !== null && row.delta !== undefined
        ? `Delta ${(row.delta >= 0 ? "+" : "")}${row.delta.toFixed(2)}`
        : undefined;
      badges.push({
        label: `${String(row.phase || "").toUpperCase()} AVG`,
        value: row.overallAvg,
        note: row.overallAvg === null ? "Insufficient Ratings" : deltaNote
      });
    });

    return badges;
  }

  async buildRatingStatistics(reportAnalytics, oit) {
    let questionMap = reportAnalytics.questionsMapById || {};
    let questions = Object.keys(questionMap);
    let allPhases = this.getRatingStatPhases(oit);
    let currentPhaseSequence = this.getPhaseSequence(oit, this.props.phase);
    let userPhaseSequences = allPhases
      .map(phase => phase.sequence)
      .filter(sequence => sequence !== null && sequence !== undefined && (currentPhaseSequence === null || Number(sequence) <= Number(currentPhaseSequence)));
    let departmentPhaseSequences = allPhases
      .map(phase => phase.sequence)
      .filter(sequence => sequence !== null && sequence !== undefined);

    if (!questions.length || !departmentPhaseSequences.length) {
      return this.emptyRatingStatistics();
    }

    let basePayload = {
      questions,
      phase_sequence: departmentPhaseSequences,
      type: this.state.selectedFormTypes,
      split_sequence: true
    };
    let trainingProgramGuid = oit.trainingTracker
      && oit.trainingTracker.trainingProgram
      && (oit.trainingTracker.trainingProgram.guid || oit.trainingTracker.trainingProgram.trainingProgramGuid);
    if (trainingProgramGuid) {
      basePayload.training_program_guid = trainingProgramGuid;
    }

    try {
      let [userRows, departmentRows] = await Promise.all([
        getAverageByForm({...basePayload, user_guid: oit.guid, phase_sequence: userPhaseSequences}),
        getAverageByForm(basePayload)
      ]);
      return this.normalizeRatingStatistics(userRows || [], departmentRows || [], allPhases, questionMap);
    } catch (err) {
      console.log("OIT summary rating statistics error", err);
      return this.emptyRatingStatistics();
    }
  }

  emptyRatingStatistics() {
    return {
      userRows: [],
      departmentRows: [],
      phaseMeta: [],
      userByPhase: {},
      departmentByPhase: {},
      userByQuestionPhase: {},
      departmentByQuestionPhase: {},
      departmentByQuestion: {}
    };
  }

  normalizeRatingStatistics(userRows, departmentRows, allPhases, questionMap) {
    let phasesBySequence = (allPhases || []).reduce((acc, phase) => ({
      ...acc,
      [String(phase.sequence)]: phase
    }), {});

    return {
      userRows,
      departmentRows,
      phaseMeta: allPhases || [],
      userByPhase: this.averageRowsByPhase(userRows, phasesBySequence),
      departmentByPhase: this.averageRowsByPhase(departmentRows, phasesBySequence),
      userByQuestionPhase: this.rowsByQuestionPhase(userRows, questionMap),
      departmentByQuestionPhase: this.rowsByQuestionPhase(departmentRows, questionMap),
      departmentByQuestion: this.averageRowsByQuestion(departmentRows, questionMap)
    };
  }

  getRatingStatPhases(oit) {
    return (oit.trainingPhases || [])
      .filter(phase => phase.type !== "orientation")
      .map(phase => ({
        guid: phase.trainingPhaseGuid || phase.guid,
        header: phase.header,
        sequence: phase.sequence
      }))
      .filter(phase => phase.sequence !== null && phase.sequence !== undefined);
  }

  getPhaseSequence(oit, phase) {
    let selectedPhase = phase || {};
    let sequence = selectedPhase.sequence;
    if (sequence !== null && sequence !== undefined) {
      return sequence;
    }

    let match = (oit.trainingPhases || []).find(tp => tp.trainingPhaseGuid === selectedPhase.trainingPhaseGuid || tp.guid === selectedPhase.guid);
    return match ? match.sequence : null;
  }

  averageRowsByPhase(rows, phasesBySequence) {
    let grouped = (rows || []).reduce((acc, row) => {
      let sequence = row.sequence_id;
      let key = String(sequence);
      let list = acc[key] || [];
      return {
        ...acc,
        [key]: [...list, row]
      };
    }, {});

    return Object.keys(grouped).reduce((acc, key) => {
      let list = grouped[key];
      let scores = list.map(row => toNumber(row.average)).filter(score => score !== null);
      let phase = phasesBySequence[key] || {};
      return {
        ...acc,
        [key]: {
          sequence: key,
          phase: phase.header || `Phase ${key}`,
          average: scores.length ? roundNumber(scores.reduce((sum, score) => sum + score, 0) / scores.length, 2) : null
        }
      };
    }, {});
  }

  rowsByQuestionPhase(rows, questionMap) {
    return (rows || []).reduce((acc, row) => {
      let question = questionMap[row.question_id] || {};
      let name = question.ratingPrompt || question.prompt || row.question_id;
      let key = `${row.sequence_id}#${name}`;
      return {
        ...acc,
        [key]: {
          questionId: row.question_id,
          observation: name,
          sequence: row.sequence_id,
          average: toNumber(row.average)
        }
      };
    }, {});
  }

  averageRowsByQuestion(rows, questionMap) {
    let grouped = (rows || []).reduce((acc, row) => {
      let question = questionMap[row.question_id] || {};
      let name = question.ratingPrompt || question.prompt || row.question_id;
      let list = acc[name] || [];
      return {
        ...acc,
        [name]: [...list, toNumber(row.average)].filter(score => score !== null)
      };
    }, {});

    return Object.keys(grouped).reduce((acc, name) => {
      let scores = grouped[name];
      return {
        ...acc,
        [name]: scores.length ? roundNumber(scores.reduce((sum, score) => sum + score, 0) / scores.length, 2) : null
      };
    }, {});
  }

  buildCommandSummary(report, reportAnalytics, oit, phase) {
    let performance = report.performance || this.emptyPerformance();
    let callExposure = report.callExposure || this.buildCallExposure([]);
    let ratingStatistics = report.ratingStatistics || this.emptyRatingStatistics();
    let categories = (performance.observationBars && performance.observationBars.categories) || [];
    let performanceTrend = this.buildPerformanceTrend(performance, ratingStatistics, reportAnalytics, phase);
    let callLogsTracked = report.callLogsTracked !== false;
    let criticalExposure = callLogsTracked
      ? this.buildCriticalExposure(callExposure)
      : {applicable: false, rows: [], gaps: []};
    let officerSafetyExposure = callLogsTracked
      ? this.buildOfficerSafetyExposure(criticalExposure)
      : [];
    let performanceExceptions = this.buildPerformanceExceptions(categories, reportAnalytics, ratingStatistics, phase);
    let riskIndicators = this.buildRiskIndicators(performanceExceptions, criticalExposure, officerSafetyExposure);
    let status = this.buildProgramStatus({
      performanceTrend,
      performanceExceptions,
      tasks: report.tasks,
      riskIndicators,
      callLogsTracked,
      criticalExposure
    });
    let nextPhaseFocus = this.buildNextPhaseFocus(performanceExceptions, criticalExposure, officerSafetyExposure);

    return {
      programStatus: status,
      executiveOverview: {
        strengths: this.pickObservationNames(performanceExceptions.topImprovements).slice(0, 4),
        improvements: this.pickObservationNames(performanceExceptions.topImprovements).slice(0, 4),
        weaknesses: this.pickObservationNames(performanceExceptions.belowDepartmentAverage).slice(0, 4),
        concerns: criticalExposure.gaps.slice(0, 4),
        focusAreas: nextPhaseFocus.developmentPriorities.slice(0, 4)
      },
      performanceTrend,
      performanceExceptions,
      criticalExposure,
      officerSafetyExposure,
      riskIndicators,
      commandStaffNotes: riskIndicators.slice(0, 4).map(indicator => ({
        type: indicator.type,
        category: indicator.type === "positive" ? "Commendation" : "Command attention",
        text: indicator.text
      })),
      nextPhaseFocus
    };
  }

  buildPerformanceTrend(performance, ratingStatistics, reportAnalytics, phase) {
    let phaseRows = (ratingStatistics.phaseMeta || []).length
      ? ratingStatistics.phaseMeta
      : (reportAnalytics.phases || []).length
        ? reportAnalytics.phases
        : (performance.phaseAverages || []).map(row => ({header: row.phase, sequence: row.phase}));
    let currentSequence = phase && phase.sequence !== undefined ? String(phase.sequence) : null;
    let userByPhase = ratingStatistics.userByPhase || {};
    let departmentByPhase = ratingStatistics.departmentByPhase || {};
    let hasEndpointTrend = !!Object.keys(userByPhase).length;
    let fallbackPhaseScores = (performance.phaseAverages || []).reduce((acc, row) => ({
      ...acc,
      [row.phase]: row.overallAvg
    }), {});

    let phases = phaseRows
      .slice()
      .sort((a, b) => collator.compare(String(a.sequence), String(b.sequence)))
      .map(row => {
        let key = String(row.sequence);
        let user = userByPhase[key];
        let department = departmentByPhase[key];
        return {
          phase: row.header || (user && user.phase) || `Phase ${key}`,
          sequence: row.sequence,
          userScore: user && user.average !== null ? user.average : fallbackPhaseScores[row.header],
          departmentScore: department ? department.average : null
        };
      })
      .map(row => {
        let isAtOrBeforeCurrent = currentSequence === null
          ? true
          : Number(row.sequence) <= Number(currentSequence);
        return {
          ...row,
          userScore: isAtOrBeforeCurrent ? row.userScore : null
        };
      })
      .filter(row => row.userScore !== null && row.userScore !== undefined || row.departmentScore !== null && row.departmentScore !== undefined);

    if (!hasEndpointTrend && !phases.length) {
      phases = (performance.phaseAverages || []).map(row => ({
        phase: row.phase,
        sequence: row.phase,
        userScore: row.overallAvg,
        departmentScore: null
      }));
    }

    let currentIndex = currentSequence
      ? phases.findIndex(row => String(row.sequence) === currentSequence)
      : phases.length - 1;
    if (currentIndex < 0) {
      currentIndex = phases.length - 1;
    }
    let current = phases[currentIndex] || {};
    let previous = currentIndex > 0 ? phases[currentIndex - 1] : {};
    let overallScore = current.userScore !== undefined ? current.userScore : null;
    let previousScore = previous.userScore !== undefined ? previous.userScore : null;
    let departmentScore = current.departmentScore !== undefined ? current.departmentScore : null;
    let departmentGap = overallScore !== null && overallScore !== 0 && departmentScore !== null
      ? roundNumber(overallScore - departmentScore, 2)
      : null;

    return {
      overallScore,
      previousScore,
      departmentScore,
      departmentGap,
      overallDelta: overallScore !== null && overallScore !== 0 && previousScore !== null
        ? roundNumber(overallScore - previousScore, 2)
        : (performance.deltas ? performance.deltas.overallDelta : null),
      currentPhaseName: (current && current.phase) || performance.currentPhaseName || (phase && phase.header),
      previousPhaseName: (previous && previous.phase) || performance.previousPhaseName,
      phases
    };
  }

  buildProgramStatus({performanceTrend, performanceExceptions, tasks, riskIndicators, callLogsTracked, criticalExposure}) {
    let numericOrNull = (value) => value === null || value === undefined || value === "" ? null : toNumber(value);
    let trend = performanceTrend || {};
    let currentScore = numericOrNull(trend.overallScore);
    let overallDelta = numericOrNull(trend.overallDelta);
    let departmentGap = numericOrNull(trend.departmentGap);
    let completion = (tasks && tasks.completion) || {};
    let taskTotal = numericOrNull(completion.total) || 0;
    let completionRatio = numericOrNull(completion.ratio);
    let declining = (performanceExceptions && performanceExceptions.declining) || [];
    let belowDepartment = (performanceExceptions && performanceExceptions.belowDepartmentAverage) || [];
    let exposureGaps = callLogsTracked ? (criticalExposure.gaps || []) : [];
    let riskCount = (riskIndicators || []).filter(item => item.type === "risk").length;
    let evidence = [];
    let concernScore = 0;

    if (currentScore === null || currentScore <= 0) {
      return {
        label: "Insufficient Data",
        tone: "neutral",
        confidence: "limited",
        summary: "There is not enough current performance data to estimate overall program progress fairly.",
        keyFactors: taskTotal
          ? [`Task completion is ${completion.done || 0} of ${taskTotal}, but current rating data is unavailable.`]
          : ["Current rating and task-completion data are unavailable."],
        confidenceNote: "This estimate should be revisited after additional current-phase observations are recorded.",
        evidence,
        concernScore: null,
        riskIndicatorCount: riskCount
      };
    }

    evidence.push(`Current performance average is ${roundNumber(currentScore, 2)}.`);
    if (currentScore < 2.5) {
      concernScore += 3;
    } else if (currentScore < 3) {
      concernScore += 2;
    } else if (currentScore < 3.5) {
      concernScore += 1;
    }

    if (overallDelta !== null) {
      evidence.push(`Phase trend is ${overallDelta >= 0 ? "+" : ""}${roundNumber(overallDelta, 2)}.`);
      if (overallDelta <= -0.5) concernScore += 2;
      else if (overallDelta <= -0.2) concernScore += 1;
      else if (overallDelta >= 0.2) concernScore -= 0.5;
    }

    if (departmentGap !== null) {
      evidence.push(`Difference from the department average is ${departmentGap >= 0 ? "+" : ""}${roundNumber(departmentGap, 2)}.`);
      if (departmentGap <= -0.5) concernScore += 1;
      else if (departmentGap >= 0.5) concernScore -= 0.5;
    }

    if (taskTotal > 0 && completionRatio !== null) {
      evidence.push(`Task completion is ${completion.done || 0} of ${taskTotal}.`);
      if (tasks.status === "Lagging" || completionRatio < 0.5) concernScore += 1.5;
      else if (completionRatio < 0.75) concernScore += 0.5;
    }

    if (declining.length) {
      evidence.push(`${declining.length} declining observation ${declining.length === 1 ? "area was" : "areas were"} identified.`);
      concernScore += Math.min(1.5, declining.length * 0.5);
    }
    if (belowDepartment.length) {
      evidence.push(`${belowDepartment.length} observation ${belowDepartment.length === 1 ? "area is" : "areas are"} below the department average.`);
      concernScore += Math.min(1, belowDepartment.length * 0.25);
    }
    if (exposureGaps.length) {
      evidence.push(`${exposureGaps.length} call-exposure ${exposureGaps.length === 1 ? "category has" : "categories have"} no recorded activity.`);
      concernScore += Math.min(0.5, exposureGaps.length * 0.1);
    }

    concernScore = Math.max(0, concernScore);
    let sourceCount = [currentScore, overallDelta, departmentGap, taskTotal > 0 ? completionRatio : null]
      .filter(value => value !== null).length;
    let confidence = sourceCount >= 3 ? "strong" : sourceCount === 2 ? "moderate" : "limited";
    let status = concernScore >= 4
      ? {label: "Additional Review Suggested", tone: "risk"}
      : concernScore >= 2
        ? {label: "Monitor Progress", tone: "attention"}
        : {label: "Progressing as Expected", tone: "positive"};

    return {
      ...status,
      confidence,
      summary: status.tone === "positive"
        ? "Available indicators generally support expected progress, with continued observation appropriate as the program develops."
        : status.tone === "attention"
          ? "Available indicators suggest progress should be monitored, with attention to the identified performance or task trends."
          : "Several available indicators warrant additional review, but this estimate should be considered alongside direct supervisory observations.",
      keyFactors: evidence.slice(0, 5),
      confidenceNote: `${confidence.charAt(0).toUpperCase() + confidence.slice(1)} confidence based on ${sourceCount} available progress indicator ${sourceCount === 1 ? "source" : "sources"}.`,
      evidence,
      concernScore: roundNumber(concernScore, 2),
      riskIndicatorCount: riskCount
    };
  }

  buildPerformanceExceptions(categories, reportAnalytics, ratingStatistics, phase) {
    let currentSequence = phase && phase.sequence !== undefined ? String(phase.sequence) : null;
    let userByQuestionPhase = (ratingStatistics && ratingStatistics.userByQuestionPhase) || {};
    let departmentByQuestionPhase = (ratingStatistics && ratingStatistics.departmentByQuestionPhase) || {};
    let departmentByQuestion = (ratingStatistics && ratingStatistics.departmentByQuestion) || {};
    let rowsByObservation = {};
    (categories || []).forEach(row => {
      let userStat = currentSequence ? userByQuestionPhase[`${currentSequence}#${row.name}`] : null;
      let departmentStat = currentSequence ? departmentByQuestionPhase[`${currentSequence}#${row.name}`] : null;
      let current = userStat && userStat.average !== null ? userStat.average : row.currentPhase;
      let departmentAvg = departmentStat && departmentStat.average !== null ? departmentStat.average : departmentByQuestion[row.name];
      rowsByObservation[row.name] = {
        observation: row.name,
        previous: row.previousPhase,
        current,
        change: row.delta,
        trainee: current,
        departmentAvg,
        gap: current !== null && current !== 0 && current !== undefined && departmentAvg !== null && departmentAvg !== undefined
          ? roundNumber(current - departmentAvg, 2)
          : null
      };
    });
    Object.keys(userByQuestionPhase).forEach(key => {
      let row = userByQuestionPhase[key];
      if (String(row.sequence) !== currentSequence || rowsByObservation[row.observation]) {
        return;
      }
      let departmentStat = departmentByQuestionPhase[`${currentSequence}#${row.observation}`];
      let departmentAvg = departmentStat && departmentStat.average !== null ? departmentStat.average : departmentByQuestion[row.observation];
      rowsByObservation[row.observation] = {
        observation: row.observation,
        previous: null,
        current: row.average,
        change: null,
        trainee: row.average,
        departmentAvg,
        gap: row.average !== null && row.average !== 0 && departmentAvg !== null && departmentAvg !== undefined
          ? roundNumber(row.average - departmentAvg, 2)
          : null
      };
    });
    let rows = Object.keys(rowsByObservation).map(key => rowsByObservation[key]);
    let declining = rows
      .filter(row => row.change !== null && row.change < 0)
      .sort((a, b) => a.change - b.change)
      .slice(0, 5);
    let topImprovements = rows
      .filter(row => row.change !== null && row.change > 0)
      .sort((a, b) => b.change - a.change)
      .slice(0, 5);
    let belowDepartmentAverage = rows
      .filter(row => row.gap !== null && row.gap < 0)
      .sort((a, b) => a.gap - b.gap)
      .slice(0, 5);

    if (!belowDepartmentAverage.length && reportAnalytics && reportAnalytics.performanceExceptions) {
      belowDepartmentAverage = this.groupPerformanceExceptions(reportAnalytics.performanceExceptions).slice(0, 5);
    }
    if (!belowDepartmentAverage.length) {
      belowDepartmentAverage = rows
        .filter(row => row.current !== null && row.current !== undefined && row.current < 3)
        .sort((a, b) => a.current - b.current)
        .slice(0, 5);
    }

    return {
      declining,
      belowDepartmentAverage,
      topImprovements
    };
  }

  groupPerformanceExceptions(performanceExceptions) {
    let grouped = (performanceExceptions || []).reduce((acc, item) => {
      let category = item.category || "Observation";
      let row = acc[category] || {observation: category, count: 0, trainee: null, current: null, departmentAvg: null, gap: null};
      let score = toNumber(item.score);
      return {
        ...acc,
        [category]: {
          ...row,
          count: row.count + 1,
          trainee: score,
          current: score,
          mostRecent: item.date || row.mostRecent
        }
      };
    }, {});

    return Object.keys(grouped)
      .map(key => grouped[key])
      .sort((a, b) => (b.count || 0) - (a.count || 0));
  }

  buildCriticalExposure(callExposure) {
    let byActivity = (callExposure && callExposure.byActivity) || [];
    let findCount = (keywords) => byActivity.reduce((sum, row) => {
      return textIncludesAny(row.activityName, keywords) ? sum + (row.count || 0) : sum;
    }, 0);
    let rows = [
      {callType: "Traffic Stops", count: findCount(["Traffic", "Stop", "Radar", "Lidar"])},
      {callType: "DUI Investigations", count: findCount(["DUI", "DWI", "Impaired", "Intox"])},
      {callType: "Domestic Violence", count: findCount(["Domestic", "Family", "FV"])},
      {callType: "Assaults", count: findCount(["Assault", "Fight", "Battery"])},
      {callType: "Mental Health Calls", count: findCount(["Mental", "Crisis", "Welfare"])},
      {callType: "Disturbances", count: findCount(["Disturbance", "Noise"])},
      {callType: "Felony Arrests", count: findCount(["Felony Arrest", "Felony"])},
      {callType: "Searches", count: findCount(["Search"])},
      {callType: "Use of Force", count: findCount(["Use of Force", "Force"])},
      {callType: "Pursuits", count: findCount(["Pursuit"])},
      {callType: "High-Risk Stops", count: findCount(["High Risk", "High-Risk", "Felony Stop"])},
      {callType: "Search Warrants", count: findCount(["Search Warrant", "Warrant"])}
    ];

    return {
      rows,
      gaps: rows.filter(row => row.count === 0).map(row => row.callType)
    };
  }

  buildOfficerSafetyExposure(criticalExposure) {
    let countFor = (name) => {
      let row = (criticalExposure.rows || []).find(item => item.callType === name);
      return row ? row.count : 0;
    };
    let rows = [
      {event: "Felony Vehicle Stops", count: countFor("High-Risk Stops")},
      {event: "Foot Pursuits", count: countFor("Pursuits")},
      {event: "Physical Resistance", count: countFor("Use of Force")},
      {event: "Building Searches", count: 0},
      {event: "Search Incident to Arrest", count: countFor("Searches")},
      {event: "Domestic Violence Arrests", count: countFor("Domestic Violence")},
      {event: "Armed Subject", count: 0},
      {event: "High-Risk Stops", count: countFor("High-Risk Stops")}
    ];

    return rows.map(row => ({
      ...row,
      status: row.count > 2 ? "YES" : row.count > 0 ? "LIMITED" : "NO"
    }));
  }

  buildRiskIndicators(performanceExceptions, criticalExposure, officerSafetyExposure) {
    let indicators = [];
    (performanceExceptions.declining || []).slice(0, 2).forEach(row => {
      indicators.push({type: "risk", text: `${row.observation} declined this phase.`});
    });
    (performanceExceptions.belowDepartmentAverage || []).slice(0, 2).forEach(row => {
      indicators.push({type: "risk", text: `${row.observation} remains a development focus.`});
    });
    (criticalExposure.gaps || []).slice(0, 3).forEach(gap => {
      indicators.push({type: "risk", text: `No exposure recorded for ${gap}.`});
    });
    (performanceExceptions.topImprovements || []).slice(0, 2).forEach(row => {
      indicators.push({type: "positive", text: `Strong improvement in ${row.observation}.`});
    });

    if (!indicators.length && (officerSafetyExposure || []).find(row => row.status === "YES")) {
      indicators.push({type: "positive", text: "Officer safety exposure is developing in recorded activity."});
    }

    return indicators.slice(0, 6);
  }

  buildNextPhaseFocus(performanceExceptions, criticalExposure) {
    let developmentPriorities = [
      ...this.pickObservationNames(performanceExceptions.declining),
      ...this.pickObservationNames(performanceExceptions.belowDepartmentAverage),
      ...criticalExposure.gaps
    ].filter((item, index, items) => items.indexOf(item) === index).slice(0, 3);

    return {
      developmentPriorities,
      exposureGoals: (criticalExposure.gaps || []).slice(0, 4).map(gap => ({
        name: gap,
        target: gap === "Felony Arrests" || gap === "High-Risk Stops" ? "1-2" : "1+"
      })),
      trainingRemediation: developmentPriorities.map(item => `Review policy / scenario training: ${item}`).slice(0, 5)
    };
  }

  pickObservationNames(rows) {
    return (rows || []).map(row => row.observation || row.callType || row.event || row).filter(row => row);
  }

  buildCallExposure(logs) {
    let normalizedLogs = (logs || []).map(log => {
      let total = (log.rows || []).reduce((sum, row) => sum + (row.count || 0), 0);
      return {
        ...log,
        rows: (log.rows || []).map(row => ({
          ...row,
          pct: row.pct !== undefined ? row.pct : percent(row.count || 0, total)
        }))
      };
    });
    let totalCalls = normalizedLogs.reduce((max, log) => {
      let total = (log.rows || []).reduce((sum, row) => sum + (row.count || 0), 0);
      return Math.max(max, total);
    }, 0);
    let activityLog = this.findLog(normalizedLogs, ["activity", "occurrence", "call type"]);
    let arrestLog = this.findLog(normalizedLogs, ["arrest"]);
    let dispositionLog = this.findLog(normalizedLogs, ["disposition"]);
    let byActivity = this.toGroupedRows(activityLog || normalizedLogs[0], "activityName", inferActivityName);
    let activityTotal = byActivity.reduce((sum, row) => sum + row.count, 0);
    byActivity = byActivity.map(row => ({...row, pct: percent(row.count, activityTotal || totalCalls)}));

    let byArrestStatus = this.toGroupedRows(arrestLog, "arrestStatus", value => {
      if (textIncludesAny(value, ["no arrest", "none"])) return "No Arrest";
      if (textIncludesAny(value, ["arrest"])) return "Arrest";
      return "Unknown";
    });
    let arrestTotal = byArrestStatus.reduce((sum, row) => sum + row.count, 0);
    byArrestStatus = ["Arrest", "No Arrest", "Unknown"].map(status => {
      let row = byArrestStatus.find(item => item.arrestStatus === status);
      return {
        arrestStatus: status,
        count: row ? row.count : 0,
        pct: percent(row ? row.count : 0, arrestTotal || totalCalls)
      };
    });

    let byDisposition = this.toGroupedRows(dispositionLog, "disposition", value => value || "Unknown");
    let dispositionTotal = byDisposition.reduce((sum, row) => sum + row.count, 0);
    byDisposition = byDisposition.map(row => ({...row, pct: percent(row.count, dispositionTotal || totalCalls)}));

    let liabilityTotals = byActivity.reduce((acc, row) => {
      let level = liabilityLevel(row.activityName);
      return {
        ...acc,
        [level]: (acc[level] || 0) + row.count
      };
    }, {Low: 0, Moderate: 0, High: 0});

    let liabilityBreakdown = ["Low", "Moderate", "High"].map(level => ({
      level,
      count: liabilityTotals[level] || 0,
      pct: percent(liabilityTotals[level] || 0, activityTotal || totalCalls)
    }));

    return {
      totalCalls,
      byActivity,
      byArrestStatus,
      byDisposition,
      activityByArrestMatrix: [],
      topActivities: byActivity
        .slice()
        .sort((a, b) => (b.count || 0) - (a.count || 0))
        .slice(0, 5),
      liabilityBreakdown,
      rawLogs: normalizedLogs
    };
  }

  findLog(logs, names) {
    return (logs || []).find(log => textIncludesAny(log.name, names));
  }

  toGroupedRows(log, prop, transform) {
    if (!log || !log.rows) {
      return [];
    }

    let totals = log.rows.reduce((acc, row) => {
      let key = transform(row.type);
      return {
        ...acc,
        [key]: (acc[key] || 0) + (row.count || 0)
      };
    }, {});
    let total = Object.keys(totals).reduce((sum, key) => sum + totals[key], 0);

    return Object.keys(totals)
      .map(key => ({
        [prop]: key,
        count: totals[key],
        pct: percent(totals[key], total)
      }))
      .sort((a, b) => (b.count || 0) - (a.count || 0));
  }

  buildTasks(oit, phase) {
    let phaseNames = (oit.trainingPhases || []).filter(tp => tp.type === "observation").map(tp => tp.header);
    let currentPhaseIndex = Math.max(0, phaseNames.findIndex(name => normalizePhaseName(name) === normalizePhaseName(phase.header)));
    let taskGroups = this.currentTaskTraining || [];
    let flatGroups = this.flattenTaskGroups(taskGroups);
    let allTasks = flatGroups.reduce((acc, group) => {
      return [...acc, ...(group.tasks || []).map(task => ({task, group}))];
    }, []);
    let completedTasks = allTasks.filter(row => this.isTaskComplete(row.task)).length;
    let totalTasks = allTasks.length;
    let incomplete = allTasks
      .filter(row => !this.isTaskComplete(row.task))
      .map(row => {
        let phaseExpected = this.getExpectedPhase(row.group, row.task, phaseNames);
        let expectedPhaseIndex = this.getPhaseIndex(phaseExpected, phaseNames);
        let lagging = totalTasks > 0 && currentPhaseIndex >= expectedPhaseIndex;
        return {
          task: row.task.prompt,
          phaseExpected,
          groupName: row.group.name,
          lagging,
          liability: liabilityLevel(row.task.prompt)
        };
      })
      .sort((a, b) => {
        if (a.lagging !== b.lagging) return a.lagging ? -1 : 1;
        if (a.liability !== b.liability) return a.liability === "High" ? -1 : 1;
        return collator.compare(a.task, b.task);
      });

    let incompleteGroups = flatGroups
      .map(group => {
        let groupTasks = (group.tasks || []).filter(task => !this.isTaskComplete(task));
        if (!groupTasks.length) {
          return null;
        }
        return {
          groupName: group.name,
          phaseExpected: this.getExpectedPhase(group, groupTasks[0], phaseNames),
          tags: this.getTagNames(group.tags),
          incompleteTasks: groupTasks.map(task => ({
            task: task.prompt,
            minResponses: task.min_responses || 1,
            completedResponses: this.completedResponses(task),
            waitingAcknowledgement: this.waitingAcknowledgement(task),
            rejectedResponses: this.rejectedResponses(task),
            substandardResponses: this.substandardResponses(task)
          }))
        };
      })
      .filter(group => group);

    let laggingCount = incomplete.filter(row => row.lagging).length;

    return {
      report: {
        status: totalTasks === 0 ? "N/A" : (laggingCount ? "Lagging" : "On Track"),
        completion: {
          done: completedTasks,
          total: totalTasks,
          ratio: totalTasks ? roundNumber(completedTasks / totalTasks, 4) : null
        },
        incompleteChecklist: incomplete.slice(0, 8).map(row => ({
          task: row.task,
          phaseExpected: row.phaseExpected
        })),
        incompleteGroups: incompleteGroups.map(group => ({
          groupName: group.groupName,
          phaseExpected: group.phaseExpected,
          incompleteCount: group.incompleteTasks.length
        }))
      },
      aiGroups: incompleteGroups
    };
  }

  flattenTaskGroups(groups) {
    return (groups || []).reduce((acc, group) => {
      return [
        ...acc,
        group,
        ...this.flattenTaskGroups(group.subGroups || [])
      ];
    }, []);
  }

  getTagNames(tags) {
    return (tags || []).map(tag => tag.name || tag.label || tag.title).filter(tag => tag);
  }

  getExpectedPhase(group, task, phaseNames) {
    let tags = [
      ...this.getTagNames(task.tags),
      ...this.getTagNames(group.tags)
    ];
    let matched = phaseNames.find(phaseName => {
      return tags.find(tag => normalizePhaseName(tag) === normalizePhaseName(phaseName));
    });

    if (matched) {
      return matched;
    }
    if (group.phase && group.phase.name) {
      return group.phase.name;
    }
    return phaseNames.length ? phaseNames[phaseNames.length - 1] : "End of Program";
  }

  getPhaseIndex(phaseName, phaseNames) {
    let index = phaseNames.findIndex(name => normalizePhaseName(name) === normalizePhaseName(phaseName));
    if (index > -1) {
      return index;
    }
    return Math.max(0, phaseNames.length - 1);
  }

  completedResponses(task) {
    return (task.taskResponses || []).filter(response => response.status === "completed" && !response.substandard).length;
  }

  waitingAcknowledgement(task) {
    return (task.taskResponses || []).filter(response => !response.acknowledgedAt && response.status === "submitted").length;
  }

  rejectedResponses(task) {
    return (task.taskResponses || []).filter(response => response.status === "rejected").length;
  }

  substandardResponses(task) {
    return (task.taskResponses || []).filter(response => response.substandard).length;
  }

  isTaskComplete(task) {
    return this.completedResponses(task) >= (task.min_responses || 1);
  }

  getGroups(lr) {
    let groupBy = lr.logField.groupBy;
    if(!Array.isArray(groupBy)) {
      groupBy = groupBy !== null ? [groupBy]: []
    }
    return groupBy.filter(gb => gb !== null)
  }

  getGroupName(lr, group) {
    try {
      return lr.logField.fields[group].label
    } catch (ex) {
      return "N/A"
    }
  }

  groupLogs(lr, groupBy, user) {
    let logReport = lr;

    let scoreField = logReport.logField.fields.findIndex(f => f.type === "score");

    let groupByMap = logReport.logField.fields[groupBy]
      ? logReport.logField.fields[groupBy].options.reduce((acc, o) => ({...acc, [o.value]: []}), {})
      : {}
    let groupedLogs = logReport.list
      .filter((log) => {
        if(this.props.selectedPhasesMap) {
          return this.props.selectedPhasesMap[log.trainingPhaseGuid]
        }
        return true
      })
      .reduce((acc, log) => {
        if(log[groupBy] && log.groupBy && ((Array.isArray(log.groupBy) && log.groupBy.includes(groupBy)) || (!Array.isArray(log.groupBy) && log.groupBy === groupBy))) {
          if(!this.props.searchValue || (this.props.searchValue && log[groupBy].includes(this.props.searchValue))) {
            if(scoreField > -1) {
              log.score = log[scoreField]
            }
            return {
              ...acc,
              [log[groupBy]]: [
                ...(acc[log[groupBy]] || []),
                log
              ]
            }
          }
          return acc
        }
        return acc
      }, groupByMap);

    let groupLogs = Object.keys(groupedLogs)
      .map((key) => ({
        key,
        count: groupedLogs[key].length,
        list: groupedLogs[key],
        hasScore: scoreField > -1,
        totalScored: groupedLogs[key].filter(l => l.score != null && l.score != undefined).length,
        totalScore: groupedLogs[key].filter(l => l.score != null && l.score != undefined).reduce((sum, l) => sum + l.score, 0)
      }))
      .sort((q1, q2) => {
        let sortPropA = q1.key;
        let sortPropB = q2.key;
        return collator.compare(sortPropA, sortPropB);
      })
      .map((l) => ({
        ...l,
        key: l.key.split("_").join(" ")
      }))
      .filter((l) => {
        if(this.state.showAllRecords) {
          return true
        }
        return l.count > 0
      });

    return groupLogs;
  }

  percentage(groupedLogs, count) {
    let total = groupedLogs.reduce((sum, gl) => sum + gl.count, 0)
    if(!total) {
      return 0
    }

    return Math.round((count/total) * 100)
  }

  get currentTaskTraining() {
    return this.props.oit.guid
      && this.props.taskTrainingMap
      && this.props.taskTrainingMap[this.props.oit.guid]
      && createSubgroups(this.props.taskTrainingMap[this.props.oit.guid]);
  }

  render() {
    return (
      <div className="oit-summary-page">
        {this.state.isLoading && <OitSummaryLoading isLoading={true} intervalMs={6000}/>}
        {!this.state.isLoading && (
          <React.Fragment>
            <div ref={this.reportRef}>
              <OitSummaryReport report={this.state.report}/>
            </div>
            <div style={{display: "flex", justifyContent: "flex-end", padding: "16px 8px 8px"}}>
              <button
                type="button"
                className="button primary"
                disabled={!this.state.isPdfLibraryReady || this.state.isSavingPdf}
                title={!this.state.isPdfLibraryReady ? "Preparing PDF tools..." : "Save this report to the phase files"}
                onClick={() => this.savePdf()}
              >
                {this.state.isSavingPdf ? "Saving..." : "Save"}
              </button>
            </div>
          </React.Fragment>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    userContext: state.common.userContext,
    forms: state.forms.list,
    settings: state.departmentSettings.settings,
    taskTrainingMap: state.taskTraining.taskTrainingMap,
  }
};

function mapDispatchToProps(dispatch) {
  return {
    fetchTaskTraining: (oitGuid) => {
      return dispatch(taskTrainingActionCreators.fetchTaskTraining(oitGuid))
    },
    addSavedSuccessfully: (message) => {
      return dispatch(notificationsActionsCreator.addSavedSuccessfully(message))
    },
    addError: (message) => {
      return dispatch(notificationsActionsCreator.addError(message))
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OitSummaryPage);
